/*
 * <loop-video>: plays a muted decorative loop only while it is on screen and
 * the tab is visible. Used by how-to-use, box-breakdown, pack-spotlight and
 * pack-feature-row. The <video> has preload="none" and no autoplay, so
 * nothing downloads until it nears the viewport.
 *
 * It does its own IntersectionObserver instead of relying on the global
 * `video.lazy` loader because pauseAllMedia() (media gallery, deferred
 * media, modals) pauses every <video> on the page and never resumes
 * decorative loops; this element resumes as long as it is visible.
 *
 * It never re-plays in a hidden tab: browsers pause background video
 * themselves, and fighting that keeps decoding video nobody can see. It
 * resumes when the tab comes back.
 *
 * With reduced motion requested it never plays (or downloads); the poster
 * shows instead.
 */
if (!customElements.get('loop-video')) {
  customElements.define(
    'loop-video',
    class LoopVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.visible = false;
        this.onPause = () => {
          if (this.shouldPlay() && !this.video.ended) {
            window.setTimeout(() => this.shouldPlay() && this.play(), 50);
          }
        };
        this.onVisibilityChange = () => {
          if (this.shouldPlay()) this.play();
        };
        this.video.addEventListener('pause', this.onPause);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (!('IntersectionObserver' in window)) {
          this.visible = true;
          this.play();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.visible = entry.isIntersecting;
              if (this.shouldPlay()) this.play();
              else if (!this.visible) this.video.pause();
            });
          },
          { rootMargin: '200px 0px', threshold: 0 }
        );
        this.observer.observe(this.video);
      }

      disconnectedCallback() {
        this.visible = false;
        if (this.observer) this.observer.disconnect();
        if (this.video) this.video.removeEventListener('pause', this.onPause);
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      shouldPlay() {
        return this.visible && document.visibilityState === 'visible';
      }

      play() {
        if (this.reducedMotion.matches || !this.video.paused) return;
        if (this.video.readyState === 0) this.video.load();
        const attempt = this.video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
      }
    }
  );
}
